import { useCallback, useEffect } from 'react';
import {
  TKycUserInfo,
  useUpdateUserKycRecord as useUpdateUserKycMutation,
} from '@xhype/card-app-sdk';
import * as Sentry from '@sentry/react';
import { useSearchParams } from 'react-router-dom';

/* Types */
type TUseHandleUpdateUserKycProps = {
  id: number | undefined;
  kycUserInfo: TKycUserInfo | undefined;
  isLoadingKycUserInfo: boolean;
};

/* Hook */
export const useHandleUpdateUserKyc = ({
  id,
  kycUserInfo,
  isLoadingKycUserInfo,
}: TUseHandleUpdateUserKycProps) => {
  const [searchParams] = useSearchParams();

  /* Mutations */
  const updateUserKycRecord = useUpdateUserKycMutation({
    onError: (error: any) => {
      console.log(
        `Update user kyc record: Unexpected error - ${error.message}`,
      );
    },
    onSuccess: () => {
      console.log('Update user kyc record: Successfully updated');
    },
  });

  /* Callbacks */
  const handleUpdateUserKyc = useCallback(
    async (userId: number, token: string) => {
      if (id !== userId) return;
      if (kycUserInfo?.status !== 'PENDING') return;

      try {
        await updateUserKycRecord.mutateAsync({
          userId: id,
          token: token,
          status: 'APPROVED',
        });
      } catch (error) {
        Sentry.captureException({
          message: `Failed to update user ${userId} kyc`,
          extra: {
            error,
          },
        });
      }
    },
    [id, updateUserKycRecord, kycUserInfo],
  );

  /* Effects */
  useEffect(() => {
    const token = searchParams.get('token');
    const userId = searchParams.get('user_id');

    if (!isLoadingKycUserInfo && kycUserInfo && id && token && userId) {
      handleUpdateUserKyc(Number(userId), token);
    }
  }, [
    handleUpdateUserKyc,
    isLoadingKycUserInfo,
    id,
    searchParams,
    kycUserInfo,
  ]);
};
